import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";
import { UserCommunicationTypeFormDto } from "../types";
import { TranslateDescription } from "../../../../types/TranslateDescription";
import { getUserCommunicationTypeFormSchema } from "../schema/userCommunicationTypeSchema";
import { createUserCommunicationType, getUserCommunicationTypeById, updateUserCommunicationType } from "../api/userCommunicationTypeApi";

export default function UserCommunicationTypeForm() {
    const { t } = useTranslation(['common', 'userCommunicationType']);
    const navigate = useNavigate();

    const { userCommunicationTypeId } = useParams<{ userCommunicationTypeId: string }>();
    const isEditMode = Boolean(userCommunicationTypeId);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });
    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});

    const [formData, setFormData] = useState<UserCommunicationTypeFormDto>({
        id: 0,
        description: {},
        canUpdateResponseDate: true
    })

    const changeInputField = (field: keyof UserCommunicationTypeFormDto, value: UserCommunicationTypeFormDto[keyof UserCommunicationTypeFormDto]) => {
        setFormData((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const changeDescriptionField = (language: keyof TranslateDescription) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setFormData((prev) => ({
            ...prev,
            description: {
                ...prev.description,
                [language]: e.target.value
            }
        }));
    };

    useEffect(() => {
        if (userCommunicationTypeId) {
            getUserCommunicationTypeById(parseInt(userCommunicationTypeId))
                .then(data => setFormData(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [userCommunicationTypeId]);

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getUserCommunicationTypeFormSchema(t);

        try {
            await schema.validate(formData, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEditMode) {
                await updateUserCommunicationType(formData.id, formData);
                navigate('/admin/userCommunicationTypes', {
                    state: { message: t("userCommunicationType:new.newUserCommunicationTypeSaveSuccessfully"), shouldRefresh: true }
                });
            }else{
                await createUserCommunicationType(formData);
                navigate('/admin/userCommunicationTypes', {
                    state: { message: t("userCommunicationType:new.newUserCommunicationTypeSaveSuccessfully"), shouldRefresh: true }
                });
            }
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });
        }
    }

    return (
        <>
            <Row className="mb-2">
                <Col md={8}>
                    <h2>{isEditMode
                        ? t("userCommunicationType:title")
                        : t("userCommunicationType:buttonNew")}</h2>
                </Col>
                <Col xs={4} md={4} className="text-end">
                    <GenericSaveButton
                        onClick={handleSubmit}
                        onCancelClick={() => navigate('/admin/userCommunicationTypes')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonBack") : t("common:buttons.buttonCancel")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group>
                                    <Form.Label>{t("common:form.fields.id")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={userCommunicationTypeId}
                                        disabled={true}
                                    />
                                </Form.Group>
                            </Col>                        
                            <Col md={6} className="d-flex align-items-end">
                                <Form.Group>
                                    <Form.Check
                                        type="checkbox"
                                        id="can-update-response-date"
                                        label={t("userCommunicationType:form.updateResponseDate")}
                                        checked={formData.canUpdateResponseDate}
                                        onChange={(e) => changeInputField("canUpdateResponseDate", e.target.checked)}
                                        isInvalid={!!errors["canUpdateResponseDate"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.canUpdateResponseDate || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionCA")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.catalan}
                                        onChange={changeDescriptionField('catalan')}
                                        isInvalid={!!errors["description.catalan"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.catalan"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionES")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.spanish}
                                        onChange={changeDescriptionField('spanish')}
                                        isInvalid={!!errors["description.spanish"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.spanish"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionEN")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.english}
                                        onChange={changeDescriptionField('english')}
                                        isInvalid={!!errors["description.english"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.english"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row className="mt-2">
                            <Col md={12}>
                                <Form.Group>
                                    <Form.Label>{t("genericDic:field1NewFormDescriptionFR")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={formData.description.french}
                                        onChange={changeDescriptionField('french')}
                                        isInvalid={!!errors["description.french"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors["description.french"]}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}

